/**
 * Route tree — centralized, type-safe route definitions.
 *
 * Structure:
 *   __root      AppShell (eager — sidebar, header, footer)
 *   ├── /                  HomePage (public)
 *   ├── /login             LoginPage (public, redirects to GitHub)
 *   ├── /waitlist          WaitlistPage (public, coming-soon + email capture)
 *   ├── /auth/callback     AuthCallbackPage (public)
 *   ├── /privacy           PrivacyPage (public)
 *   ├── /terms             TermsPage (public)
 *   ├── _auth              AuthGuard (protected section)
 *   │   ├── /onboard                              OnboardIdeaPage (standalone — no project ID yet)
 *   │   ├── /projects                             ProjectsPage
 *   │   ├── /projects/:id                         ProjectDetailPage
 *   │   ├── /projects/:id/onboard                 OnboardWizardPage (layout)
 *   │   │   ├── /projects/:id/onboard/interview       OnboardInterviewPage
 *   │   │   ├── /projects/:id/onboard/deep-research  OnboardDeepResearchPage
 *   │   │   ├── /projects/:id/onboard/strawperson     OnboardStrawpersonPage
 *   │   │   ├── /projects/:id/onboard/verdict         OnboardVerdictPage
 *   │   │   ├── /projects/:id/onboard/kb              OnboardKBReviewPage
 *   │   │   └── /projects/:id/onboard/connect         OnboardConnectPage
 *   │   ├── /projects/:id/next-steps              NextStepsPage
 *   │   ├── /projects/:id/drafts/:draftId         DraftDetailPage
 *   │   ├── /projects/:id/drafts/:draftId/diff    DiffViewerPage
 *   │   ├── /projects/:id/kb                      KBEditorPage
 *   │   ├── /account                              AccountPage
 *   │   ├── /settings/github                      GitHubSettingsPage
 *   │   ├── /github/callback                      GitHubCallbackPage
 *   │   ├── /admin/users                          AdminUsersPage
 *   │   ├── /admin/users/:userId                  AdminUserDetailPage
 *   │   ├── /admin/orgs                           AdminOrgsPage
 *   │   ├── /admin/orgs/:orgId                    AdminOrgDetailPage
 *   │   └── /admin/feedback                       AdminFeedbackPage
 *   └── *       NotFoundPage (catch-all)
 *
 * All page components are lazy-loaded via React.lazy() so the initial bundle
 * only contains the app shell and routing logic.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:ROUTING
 */
import { lazy } from 'react'
import { z } from 'zod'
import {
  createRouter,
  createRoute,
  createRootRoute,
} from '@tanstack/react-router'
import { AppShell } from '@/components/layout/AppShell/AppShell'
import { AuthGuard } from '@/components/layout/AuthGuard/AuthGuard'

// ── Lazy page imports ─────────────────────────────────────────────────────────
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })))
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
)
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })))
const WaitlistPage = lazy(() =>
  import('@/pages/WaitlistPage').then((m) => ({ default: m.WaitlistPage }))
)
const AuthCallbackPage = lazy(() =>
  import('@/pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage }))
)
const PrivacyPage = lazy(() =>
  import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
)
const TermsPage = lazy(() =>
  import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage }))
)
const ProjectsPage = lazy(() =>
  import('@/pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage }))
)
const ProjectDetailPage = lazy(() =>
  import('@/pages/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage }))
)
const DraftDetailPage = lazy(() =>
  import('@/pages/DraftDetailPage').then((m) => ({ default: m.DraftDetailPage }))
)
const DiffViewerPage = lazy(() =>
  import('@/pages/DiffViewerPage').then((m) => ({ default: m.DiffViewerPage }))
)
const KBEditorPage = lazy(() =>
  import('@/pages/KBEditorPage').then((m) => ({ default: m.KBEditorPage }))
)

const AccountPage = lazy(() =>
  import('@/pages/AccountPage').then((m) => ({ default: m.AccountPage }))
)

const GitHubCallbackPage = lazy(() =>
  import('@/pages/GitHubCallbackPage').then((m) => ({ default: m.GitHubCallbackPage }))
)
const GitHubSettingsPage = lazy(() =>
  import('@/pages/GitHubSettingsPage').then((m) => ({ default: m.GitHubSettingsPage }))
)

const NextStepsPage = lazy(() =>
  import('@/pages/NextStepsPage').then((m) => ({ default: m.NextStepsPage }))
)

// Onboarding
const OnboardIdeaPage = lazy(() =>
  import('@/pages/OnboardIdeaPage').then((m) => ({ default: m.OnboardIdeaPage }))
)
const OnboardWizardPage = lazy(() =>
  import('@/pages/OnboardWizardPage').then((m) => ({ default: m.OnboardWizardPage }))
)
const OnboardKBReviewPage = lazy(() =>
  import('@/pages/OnboardKBReviewPage').then((m) => ({ default: m.OnboardKBReviewPage }))
)
const OnboardInterviewPage = lazy(() =>
  import('@/pages/OnboardInterviewPage').then((m) => ({ default: m.OnboardInterviewPage }))
)
const OnboardVerdictPage = lazy(() =>
  import('@/pages/OnboardVerdictPage').then((m) => ({ default: m.OnboardVerdictPage }))
)
const OnboardDeepResearchPage = lazy(() =>
  import('@/pages/OnboardDeepResearchPage').then((m) => ({ default: m.OnboardDeepResearchPage }))
)
const OnboardStrawpersonPage = lazy(() =>
  import('@/pages/OnboardStrawpersonPage').then((m) => ({ default: m.OnboardStrawpersonPage }))
)
const OnboardConnectPage = lazy(() =>
  import('@/pages/OnboardConnectPage').then((m) => ({ default: m.OnboardConnectPage }))
)

// Admin
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage }))
)
const AdminUserDetailPage = lazy(() =>
  import('@/pages/admin/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage }))
)
const AdminOrgsPage = lazy(() =>
  import('@/pages/admin/AdminOrgsPage').then((m) => ({ default: m.AdminOrgsPage }))
)
const AdminOrgDetailPage = lazy(() =>
  import('@/pages/admin/AdminOrgDetailPage').then((m) => ({ default: m.AdminOrgDetailPage }))
)
const AdminFeedbackPage = lazy(() =>
  import('@/pages/admin/AdminFeedbackPage').then((m) => ({ default: m.AdminFeedbackPage }))
)

// ── Route definitions ─────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: () => <NotFoundPage />,
})

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: z.object({
    invite: z.string().optional(),
  }),
})

const waitlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/waitlist',
  component: WaitlistPage,
  validateSearch: z.object({
    blocked: z.boolean().optional(),
  }),
})

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
})

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
})

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsPage,
})

// ── Auth-guarded layout ───────────────────────────────────────────────────────

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: AuthGuard,
})

// ── Onboarding: standalone idea page (no project ID yet) ─────────────────────

const onboardIdeaRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/onboard',
  component: OnboardIdeaPage,
})

// ── Onboarding: project-scoped stepper (layout + children) ───────────────────

const projectOnboardRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects/$id/onboard',
  component: OnboardWizardPage,
})

const projectOnboardInterviewRoute = createRoute({
  getParentRoute: () => projectOnboardRoute,
  path: '/interview',
  component: OnboardInterviewPage,
})

const projectOnboardVerdictRoute = createRoute({
  getParentRoute: () => projectOnboardRoute,
  path: '/verdict',
  component: OnboardVerdictPage,
})

const projectOnboardKBRoute = createRoute({
  getParentRoute: () => projectOnboardRoute,
  path: '/kb',
  component: OnboardKBReviewPage,
  validateSearch: z.object({
    file: z.string().optional(),
  }),
})

const projectOnboardDeepResearchRoute = createRoute({
  getParentRoute: () => projectOnboardRoute,
  path: '/deep-research',
  component: OnboardDeepResearchPage,
})

const projectOnboardStrawpersonRoute = createRoute({
  getParentRoute: () => projectOnboardRoute,
  path: '/strawperson',
  component: OnboardStrawpersonPage,
})

const projectOnboardConnectRoute = createRoute({
  getParentRoute: () => projectOnboardRoute,
  path: '/connect',
  component: OnboardConnectPage,
})

// ── Project routes ───────────────────────────────────────────────────────────

const projectsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects',
  component: ProjectsPage,
})

const projectDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects/$id',
  component: ProjectDetailPage,
})

const draftDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects/$id/drafts/$draftId',
  component: DraftDetailPage,
})

const diffViewerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects/$id/drafts/$draftId/diff',
  component: DiffViewerPage,
})

const nextStepsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects/$id/next-steps',
  component: NextStepsPage,
  validateSearch: z.object({
    file: z.string().optional(),
  }),
})

const kbEditorRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/projects/$id/kb',
  component: KBEditorPage,
  validateSearch: z.object({
    file: z.string().optional(),
  }),
})

const accountRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/account',
  component: AccountPage,
  validateSearch: z.object({
    status: z.string().optional(),
    session_id: z.string().optional(),
  }),
})

const gitHubCallbackRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/github/callback',
  component: GitHubCallbackPage,
})

const gitHubSettingsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/settings/github',
  component: GitHubSettingsPage,
})

// ── Admin routes ────────────────────────────────────────────────────────────

const adminUsersRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin/users',
  component: AdminUsersPage,
  validateSearch: z.object({
    page: z.number().optional(),
  }),
})

const adminUserDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin/users/$userId',
  component: AdminUserDetailPage,
})

const adminOrgsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin/orgs',
  component: AdminOrgsPage,
  validateSearch: z.object({
    page: z.number().optional(),
  }),
})

const adminOrgDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin/orgs/$orgId',
  component: AdminOrgDetailPage,
})

const adminFeedbackRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin/feedback',
  component: AdminFeedbackPage,
  validateSearch: z.object({
    page: z.number().optional(),
  }),
})

// ── Route tree ────────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  homeRoute,
  loginRoute,
  waitlistRoute,
  authCallbackRoute,
  privacyRoute,
  termsRoute,
  authRoute.addChildren([
    onboardIdeaRoute,
    projectOnboardRoute.addChildren([
      projectOnboardInterviewRoute,
      projectOnboardDeepResearchRoute,
      projectOnboardStrawpersonRoute,
      projectOnboardVerdictRoute,
      projectOnboardKBRoute,
      projectOnboardConnectRoute,
    ]),
    projectsRoute,
    projectDetailRoute,
    nextStepsRoute,
    draftDetailRoute,
    diffViewerRoute,
    kbEditorRoute,
    accountRoute,
    gitHubCallbackRoute,
    gitHubSettingsRoute,
    adminUsersRoute,
    adminUserDetailRoute,
    adminOrgsRoute,
    adminOrgDetailRoute,
    adminFeedbackRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Type augmentation for @tanstack/react-router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
